import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, MessageCircle, Send, Trash2, Globe, Newspaper, Brain, BarChart2, TrendingUp, Sparkles, CheckCircle, XCircle } from 'lucide-react';
import { GRADE_COLOR, resolvePlaybookFromRegime, deriveLiteRegimeFromCues } from '../utils/playbookLogic';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LIFECYCLE_META = {
  trade_idea: { label: 'Trade Idea', color: '#6366f1' },
  doubt:      { label: 'Doubt',      color: '#f59e0b' },
  backtest:   { label: 'Backtest',   color: '#8b5cf6' },
  profit:     { label: 'Profit',     color: '#16c784' },
  loss:       { label: 'Loss',       color: '#ef4444' },
  mistake:    { label: 'Mistake',    color: '#f97316' },
};


function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Attachment card renderers ──────────────────────────────────────────────────

function BacktestCard({ snapshot: s }) {
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthly = Array.isArray(s.monthly_returns) ? s.monthly_returns : [];
  const trades  = Array.isArray(s.trades) ? s.trades : [];
  const years   = [...new Set(monthly.map(m => m.year))].sort();
  const lookup  = {};
  monthly.forEach(m => { lookup[`${m.year}-${m.month}`] = m; });

  return (
    <div style={{ borderRadius: 12, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', marginBottom: 8, overflow: 'hidden' }}>
      {/* Summary metrics */}
      <div style={{ padding: '12px 12px 10px' }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Backtest — {s.symbol || s.name || ''}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {[
            ['Return',   s.total_return_pct != null ? `${parseFloat(s.total_return_pct).toFixed(1)}%` : '—', s.total_return_pct > 0 ? '#16c784' : '#ef4444'],
            ['Win Rate', s.win_rate_pct != null ? `${parseFloat(s.win_rate_pct).toFixed(0)}%` : '—', '#eceef5'],
            ['Trades',   s.num_trades ?? '—', '#eceef5'],
            ['Sharpe',   s.sharpe_ratio != null ? parseFloat(s.sharpe_ratio).toFixed(2) : '—', '#eceef5'],
            ['Max DD',   s.max_drawdown_pct != null ? `${parseFloat(s.max_drawdown_pct).toFixed(1)}%` : '—', '#ef4444'],
          ].map(([label, val, col]) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 14, fontWeight: 800, color: col }}>{val}</p>
              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly P&L table */}
      {years.length > 0 && (
        <div style={{ borderTop: '1px solid rgba(99,102,241,0.15)', padding: '10px 12px' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Monthly Returns</p>
          {years.map(yr => (
            <div key={yr} style={{ marginBottom: 6 }}>
              <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>{yr}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3 }}>
                {monthNames.map((mn, idx) => {
                  const entry = lookup[`${yr}-${idx + 1}`];
                  const val   = entry?.return_pct != null ? parseFloat(entry.return_pct)
                              : entry?.pnl         != null ? parseFloat(entry.pnl)
                              : null;
                  const isPercent = entry?.return_pct != null;
                  const fmt = val == null ? '—'
                            : isPercent ? `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`
                            : val >= 0 ? `+₹${(Math.abs(val)/1000).toFixed(1)}k` : `-₹${(Math.abs(val)/1000).toFixed(1)}k`;
                  return (
                    <div key={mn} style={{
                      textAlign: 'center', padding: '3px 2px', borderRadius: 5,
                      background: val == null ? 'rgba(255,255,255,0.04)' : val >= 0 ? 'rgba(22,199,132,0.12)' : 'rgba(239,68,68,0.12)',
                    }}>
                      <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.3)' }}>{mn}</p>
                      <p style={{ fontSize: 9, fontWeight: 700, color: val == null ? 'rgba(255,255,255,0.2)' : val >= 0 ? '#16c784' : '#ef4444' }}>
                        {fmt}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Trade history */}
      {trades.length > 0 && (
        <div style={{ borderTop: '1px solid rgba(99,102,241,0.15)', padding: '10px 12px' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
            Trade History{trades.length > 5 ? ` (showing 5 of ${trades.length})` : ''}
          </p>
          {trades.slice(0, 5).map((t, i) => {
            const pnl    = parseFloat(t.pnl || t.pnl_amount || 0);
            const sym    = (t.symbol || s.symbol || '').replace('.NS', '');
            const dir    = t.trade_type || t.direction || (t.exit_reason ? `Exit: ${t.exit_reason}` : '');
            const date   = t.exit_date ? new Date(t.exit_date).toLocaleDateString('en-IN') : '';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < Math.min(trades.length, 5) - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#eceef5' }}>{sym}</p>
                  <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>
                    {dir}{date ? ` · ${date}` : ''}
                  </p>
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: pnl >= 0 ? '#16c784' : '#ef4444' }}>
                  {pnl >= 0 ? '+' : ''}₹{Math.abs(pnl).toLocaleString('en-IN')}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PnLSummaryCard({ snapshot: s }) {
  const pnl = parseFloat(s.total_pnl || 0);
  return (
    <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(22,199,132,0.07)', border: '1px solid rgba(22,199,132,0.2)', marginBottom: 8 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: '#16c784', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>P&L Summary</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {[
          ['Total P&L', `₹${Math.abs(pnl).toLocaleString('en-IN')}`, pnl >= 0 ? '#16c784' : '#ef4444'],
          ['Win Rate', s.win_rate_pct != null ? `${parseFloat(s.win_rate_pct).toFixed(0)}%` : '—', '#eceef5'],
          ['Trades', s.total_trades ?? '—', '#eceef5'],
          ['Avg Win', s.avg_win != null ? `₹${parseInt(s.avg_win).toLocaleString('en-IN')}` : '—', '#16c784'],
          ['Avg Loss', s.avg_loss != null ? `₹${Math.abs(parseInt(s.avg_loss)).toLocaleString('en-IN')}` : '—', '#ef4444'],
          ['Best', s.best_trade_pnl != null ? `₹${parseInt(s.best_trade_pnl).toLocaleString('en-IN')}` : '—', '#16c784'],
        ].map(([label, val, col]) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: col }}>{val}</p>
            <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DisciplineCard({ snapshot: s }) {
  return (
    <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 8 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Discipline Score</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <p style={{ fontSize: 32, fontWeight: 900, color: '#f59e0b', lineHeight: 1 }}>{s.score ?? s.discipline_score ?? '—'}</p>
        <div>
          {s.profile_type && <p style={{ fontSize: 12, fontWeight: 700, color: '#eceef5', marginBottom: 2 }}>{s.profile_type}</p>}
          {s.tier && <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{s.tier} tier</p>}
        </div>
      </div>
    </div>
  );
}

function TradeHistoryCard({ snapshot: s }) {
  const trades = Array.isArray(s) ? s : (s.trades || []);
  if (!trades.length) return null;
  return (
    <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.2)', marginBottom: 8 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Trade Records</p>
      {trades.slice(0, 5).map((t, i) => {
        const pnl = parseFloat(t.pnl || 0);
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < Math.min(trades.length, 5) - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#eceef5' }}>{t.symbol}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{t.trade_type} · {t.exit_date ? new Date(t.exit_date).toLocaleDateString('en-IN') : '—'}</p>
            </div>
            <p style={{ fontSize: 13, fontWeight: 700, color: pnl >= 0 ? '#16c784' : '#ef4444' }}>
              {pnl >= 0 ? '+' : ''}₹{Math.abs(pnl).toLocaleString('en-IN')}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function CapitalTierCard({ snapshot: s }) {
  return (
    <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.2)', marginBottom: 8 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Capital Tier</p>
      <div style={{ display: 'flex', gap: 20 }}>
        <div>
          <p style={{ fontSize: 20, fontWeight: 900, color: '#8b5cf6' }}>{s.tier || '—'}</p>
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Tier</p>
        </div>
        {s.behavior_score != null && (
          <div>
            <p style={{ fontSize: 20, fontWeight: 900, color: '#eceef5' }}>{s.behavior_score}</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Score</p>
          </div>
        )}
        {s.active_capital != null && (
          <div>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#eceef5' }}>₹{parseInt(s.active_capital).toLocaleString('en-IN')}</p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Daily Cap</p>
          </div>
        )}
      </div>
    </div>
  );
}

const DE_GRADE_COLOR = { A: '#16C784', B: '#4ecca3', C: '#f59e0b', D: '#f97316', Wait: '#8b5cf6', 'Sit Out': '#6b7280' };

function DecisionEngineCard({ snapshot: s }) {
  const gc = DE_GRADE_COLOR[s.grade] || '#eceef5';
  const steps = [
    { key: 'global',        section: 'Global Context' },
    { key: 'gap',           section: 'Nifty Gap' },
    { key: 'open_behavior', section: 'Open Behavior' },
    { key: 'setup',         section: 'Setup' },
    { key: 'instrument',    section: 'Instrument' },
  ];

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${gc}30`, marginBottom: 8 }}>

      {/* Header */}
      <div style={{ padding: '12px', background: `linear-gradient(135deg,${gc}18 0%,${gc}05 100%)`, borderBottom: `1px solid ${gc}20` }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: gc, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Decision Engine Analysis{s.sim_date ? ` — ${s.sim_date}` : ''}</p>
        {/* Breadcrumb badges */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {steps.map(({ key }) => s[key] && (
            <span key={key} style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 10, background: `${s[key].color}22`, color: s[key].color }}>
              {s[key].label}
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            {s.narrative && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, marginBottom: 6 }}>{s.narrative}</p>}
            {s.sizing && (
              <div style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 8, background: `${gc}18` }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: gc }}>{s.sizing}</p>
              </div>
            )}
          </div>
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <p style={{ fontSize: 38, fontWeight: 900, color: gc, lineHeight: 1 }}>{s.grade}</p>
            <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>Grade</p>
          </div>
        </div>
      </div>

      {/* 5-step breakdown */}
      {steps.map(({ key, section }) => {
        const st = s[key];
        if (!st) return null;
        return (
          <div key={key} style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{section}</p>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 8, background: `${st.color}22`, color: st.color }}>{st.label}</span>
            </div>
            {st.why && <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55, marginBottom: 4 }}>{st.why}</p>}
            {/* Caution (gap step) */}
            {st.caution && (
              <p style={{ fontSize: 10, fontWeight: 700, color: st.color, padding: '3px 8px', borderRadius: 6, background: `${st.color}14`, display: 'inline-block' }}>⚠ {st.caution}</p>
            )}
            {/* Signal (open behavior) */}
            {st.signal && <p style={{ fontSize: 11, fontWeight: 600, color: '#eceef5', marginTop: 2 }}>→ {st.signal}</p>}
            {/* Entry/SL/Target (setup) */}
            {(st.entry || st.sl || st.target) && (
              <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {st.entry  && <p style={{ fontSize: 11, color: '#eceef5' }}><span style={{ fontWeight: 700 }}>Entry: </span>{st.entry}</p>}
                {st.sl     && <p style={{ fontSize: 11, color: '#ef4444' }}><span style={{ fontWeight: 700 }}>SL: </span>{st.sl}</p>}
                {st.target && <p style={{ fontSize: 11, color: '#16c784' }}><span style={{ fontWeight: 700 }}>Target: </span>{st.target}</p>}
              </div>
            )}
            {/* Rules (instrument) */}
            {st.rules?.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {st.rules.slice(0, 3).map((r, i) => (
                  <p key={i} style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>• {r}</p>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Contradictions */}
      {s.contradictions?.length > 0 && (
        <div style={{ padding: '10px 12px', background: 'rgba(249,115,22,0.06)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>⚠ Conflicts Detected</p>
          {s.contradictions.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <span style={{ color: '#f97316', fontSize: 12, lineHeight: 1.4, flexShrink: 0 }}>▸</span>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>{c}</p>
            </div>
          ))}
        </div>
      )}

      {/* Global indices */}
      {s.global_indices?.length > 0 && (
        <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Global Markets That Day</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {s.global_indices.map((idx, i) => {
              const chg = parseFloat(idx.change_pct || 0);
              const col = chg > 0 ? '#16c784' : chg < 0 ? '#ef4444' : '#6b7280';
              return (
                <div key={i} style={{ minWidth: 52, textAlign: 'center' }}>
                  <p style={{ fontSize: 13, fontWeight: 800, color: col }}>{chg >= 0 ? '+' : ''}{chg.toFixed(2)}%</p>
                  <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>{idx.name || idx.symbol}</p>
                </div>
              );
            })}
          </div>
          {s.market_mood && (
            <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: `${s.market_mood.color}14` }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: s.market_mood.color }}>{s.market_mood.label}</p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{s.market_mood.summary}</p>
            </div>
          )}
        </div>
      )}

      {/* Pre-market news */}
      {s.news?.length > 0 && (
        <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Pre-Market News</p>
          {s.news.slice(0, 6).map((n, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', flexShrink: 0, marginTop: 2 }}>▸</span>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>{n.title}</p>
            </div>
          ))}
        </div>
      )}

      {/* Action plan */}
      {s.actions?.length > 0 && (
        <div style={{ padding: '10px 12px' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Action Plan</p>
          {s.actions.map((action, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5 }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${gc}20`, color: gc, fontSize: 9, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{action}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TradeAnalysisCard({ snapshot: s }) {
  const pnl = parseFloat(s.pnl || 0);
  const hasExtra = s.stop_loss || s.target_price || s.notes || s.entry_date || s.exit_date;
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(6,182,212,0.25)', marginBottom: 8 }}>
      {/* Header */}
      <div style={{ padding: '10px 12px', background: 'rgba(6,182,212,0.1)', borderBottom: '1px solid rgba(6,182,212,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Trade Analysis
          </p>
          {s.trade_type && (
            <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 5, background: s.trade_type === 'BUY' ? 'rgba(22,199,132,0.2)' : 'rgba(239,68,68,0.2)', color: s.trade_type === 'BUY' ? '#16c784' : '#ef4444' }}>
              {s.trade_type}
            </span>
          )}
        </div>
        <p style={{ fontSize: 14, fontWeight: 800, color: '#eceef5', marginTop: 2 }}>
          {s.symbol}{s.company && s.company !== s.symbol ? <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.45)' }}> · {s.company}</span> : ''}
        </p>
        {(s.entry_date || s.exit_date) && (
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
            {s.entry_date ? s.entry_date.slice(0, 10) : ''}
            {s.entry_date && s.exit_date ? ' → ' : ''}
            {s.exit_date ? s.exit_date.slice(0, 10) : ''}
            {s.holding_days ? ` · ${s.holding_days} days` : ''}
          </p>
        )}
      </div>

      {/* Key metrics */}
      <div style={{ padding: '10px 12px', borderBottom: hasExtra ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {[
            ['Entry', s.entry_price ? `₹${parseFloat(s.entry_price).toFixed(2)}` : '—', '#eceef5'],
            ['Exit',  s.exit_price  ? `₹${parseFloat(s.exit_price).toFixed(2)}`  : '—', '#eceef5'],
            ['P&L',   `${pnl >= 0 ? '+' : ''}₹${Math.abs(Math.round(pnl)).toLocaleString('en-IN')}`, pnl >= 0 ? '#16c784' : '#ef4444'],
            ['P&L %', s.pnl_pct != null ? `${parseFloat(s.pnl_pct) >= 0 ? '+' : ''}${parseFloat(s.pnl_pct).toFixed(2)}%` : '—', pnl >= 0 ? '#16c784' : '#ef4444'],
            ...(s.quantity ? [['Qty', s.quantity, '#eceef5']] : []),
          ].map(([label, val, col]) => (
            <div key={label}>
              <p style={{ fontSize: 15, fontWeight: 800, color: col, lineHeight: 1 }}>{val}</p>
              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* SL / Target */}
      {(s.stop_loss || s.target_price) && (
        <div style={{ padding: '8px 12px', display: 'flex', gap: 16, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {s.stop_loss && (
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>₹{parseFloat(s.stop_loss).toFixed(2)}</p>
              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>Stop Loss</p>
            </div>
          )}
          {s.target_price && (
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#16c784' }}>₹{parseFloat(s.target_price).toFixed(2)}</p>
              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>Target</p>
            </div>
          )}
        </div>
      )}

      {/* Strategy tags */}
      {(s.strategy_tags || []).length > 0 && (
        <div style={{ padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          {s.strategy_tags.map((tag, i) => (
            <span key={i} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>{tag}</span>
          ))}
        </div>
      )}

      {/* Notes */}
      {s.notes && (
        <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Notes</p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>{s.notes}</p>
        </div>
      )}

      {/* AI Review */}
      {s.ai_review && (
        <div style={{ padding: '10px 12px', background: 'rgba(245,158,11,0.05)' }}>
          <p style={{ fontSize: 9, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 }}>AI Review</p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>{s.ai_review}</p>
        </div>
      )}
    </div>
  );
}

const OUTCOME_META = {
  target_hit: { label: 'Target Hit',   color: '#16c784' },
  sl_hit:     { label: 'SL Hit',       color: '#ef4444' },
  manual:     { label: 'Manual Exit',  color: '#f59e0b' },
};

function PlaybookResult({ pb, lite, navigate }) {
  const gc = GRADE_COLOR[pb.grade] || '#6b7280';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Tags row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {[pb.globalLabel, pb.gapLabel, pb.openLabel, pb.setupLabel, pb.instrLabel].filter(Boolean).map((lbl, i) => (
          <span key={i} style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.1)' }}>{lbl}</span>
        ))}
      </div>

      {/* Narrative + grade */}
      <div style={{ position: 'relative', padding: '12px 14px', borderRadius: 10, background: `${gc}0a`, border: `1px solid ${gc}25` }}>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, paddingRight: 48 }}>{pb.narrative}</p>
        <div style={{ position: 'absolute', top: 10, right: 12, textAlign: 'right' }}>
          <p style={{ fontSize: 26, fontWeight: 900, color: gc, lineHeight: 1 }}>{pb.grade}</p>
          <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', fontWeight: 600, textTransform: 'uppercase' }}>session grade</p>
        </div>
      </div>

      {/* Sizing */}
      <div style={{ padding: '8px 12px', borderRadius: 8, background: `${gc}12`, border: `1px solid ${gc}30` }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: gc }}>{pb.sizing}</p>
      </div>

      {/* Contradictions */}
      {pb.contradictions?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pb.contradictions.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '5px 9px', borderRadius: 7, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
              <span style={{ color: '#ef4444', fontSize: 11, marginTop: 1, flexShrink: 0 }}>⚠</span>
              <p style={{ fontSize: 11, color: 'rgba(239,68,68,0.85)', lineHeight: 1.5 }}>{c}</p>
            </div>
          ))}
        </div>
      )}

      {/* Entry plan */}
      {(pb.setupEntry || pb.setupSL || pb.setupTarget) && (
        <div style={{ padding: '10px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <p style={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Entry Plan</p>
          {pb.setupEntry && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}><span style={{ fontWeight: 700, color: '#eceef5' }}>Entry:</span> {pb.setupEntry}</p>}
          {pb.setupSL    && <p style={{ fontSize: 12, color: 'rgba(239,68,68,0.85)',  marginBottom: 4 }}><span style={{ fontWeight: 700 }}>SL:</span> {pb.setupSL}</p>}
          {pb.setupTarget && <p style={{ fontSize: 12, color: 'rgba(22,199,132,0.85)' }}><span style={{ fontWeight: 700 }}>Target:</span> {pb.setupTarget}</p>}
        </div>
      )}

      {/* Action sequence */}
      {pb.actions?.length > 0 && (
        <div style={{ padding: '10px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p style={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Action Sequence</p>
          {pb.actions.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: gc, background: `${gc}18`, border: `1px solid ${gc}30`, borderRadius: 4, padding: '1px 5px', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{a}</p>
            </div>
          ))}
        </div>
      )}

      {/* Rules + Avoid */}
      {pb.rules?.length > 0 && (
        <div style={{ padding: '10px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p style={{ fontSize: 9, fontWeight: 800, color: '#16c784', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Rules</p>
          {pb.rules.slice(0, 5).map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'flex-start' }}>
              <CheckCircle size={11} color="#16c784" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{r}</p>
            </div>
          ))}
        </div>
      )}
      {pb.avoid?.length > 0 && (
        <div style={{ padding: '10px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p style={{ fontSize: 9, fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Avoid</p>
          {pb.avoid.slice(0, 4).map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'flex-start' }}>
              <XCircle size={11} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{a}</p>
            </div>
          ))}
        </div>
      )}

      {/* CTA */}
      {pb.calcPath && (
        <button
          onClick={() => navigate(pb.calcPath)}
          style={{ width: '100%', padding: '12px', borderRadius: 10, background: `linear-gradient(135deg,${pb.instrColor || gc},${pb.instrColor || gc}cc)`, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff' }}
        >
          Open {pb.instrLabel || 'Derivatives'} Desk →
        </button>
      )}

      {lite && <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.18)', fontStyle: 'italic', textAlign: 'center' }}>Derived from global cues — no intraday regime data for this date.</p>}
    </div>
  );
}

function TradeStoryCard({ snapshot: s }) {
  const navigate = useNavigate();
  const mc     = s.market_context || {};
  const ud     = s.user_decision  || {};
  const ts     = s.trade_setup    || {};
  const ar     = s.actual_results || {};
  const pnl    = parseFloat(ar.pnl || 0);
  const pnlPos = pnl >= 0;
  const mm     = mc.market_mood   || null;
  const out    = OUTCOME_META[ar.outcome] || OUTCOME_META.manual;
  const accentColor = pnlPos ? '#16c784' : '#ef4444';

  // Fetch regime at view time for old snapshots that don't have it embedded.
  // Prefer mc.sim_date; fall back to ts.entry_date for older snapshots.
  const regimeDate = mc.sim_date || ts.entry_date || null;
  const [regime, setRegime] = React.useState(mc.regime || null);
  const [regimeLoading, setRegimeLoading] = React.useState(false);
  const [playbookOpen, setPlaybookOpen] = React.useState(false);
  React.useEffect(() => {
    if (regime || !regimeDate) return;
    setRegimeLoading(true);
    axios.get(`${API_URL}/analytics/daily-regime`, { params: { date: regimeDate } })
      .then(r => { if (r.data) setRegime(r.data); })
      .catch(() => {})
      .finally(() => setRegimeLoading(false));
  }, [regimeDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const SectionHeader = ({ Icon, num, title, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px 7px', background: `${color}0e`, borderBottom: `1px solid ${color}18` }}>
      <div style={{ width: 20, height: 20, borderRadius: 6, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={10} color={color} />
      </div>
      <p style={{ fontSize: 10, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1 }}>{title}</p>
      <span style={{ fontSize: 9, fontWeight: 700, color: `${color}80`, background: `${color}15`, borderRadius: 4, padding: '1px 5px' }}>{num}/6</span>
    </div>
  );

  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${accentColor}30`, marginBottom: 8, background: 'rgba(255,255,255,0.02)' }}>

      {/* ── Hero header ── */}
      <div style={{ padding: '14px', background: `linear-gradient(135deg,${accentColor}18 0%,${accentColor}05 100%)`, borderBottom: `1px solid ${accentColor}20`, position: 'relative', overflow: 'hidden' }}>
        {/* Watermark */}
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 64, fontWeight: 900, color: `${accentColor}08`, lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}>
          {pnlPos ? '▲' : '▼'}
        </span>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <p style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
              Trade Story{mc.sim_date ? ` · ${mc.sim_date}` : ''}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <p style={{ fontSize: 20, fontWeight: 900, color: '#eceef5', letterSpacing: '-0.02em' }}>{ts.symbol || '—'}</p>
              {ts.company && ts.company !== ts.symbol && (
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>{ts.company}</p>
              )}
              {ts.trade_type && (
                <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: ts.trade_type === 'BUY' ? 'rgba(22,199,132,0.2)' : 'rgba(239,68,68,0.2)', color: ts.trade_type === 'BUY' ? '#16c784' : '#ef4444', border: `1px solid ${ts.trade_type === 'BUY' ? '#16c78440' : '#ef444440'}` }}>
                  {ts.trade_type}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <p style={{ fontSize: 22, fontWeight: 900, color: accentColor, lineHeight: 1, letterSpacing: '-0.02em' }}>
              {pnlPos ? '+' : ''}₹{Math.abs(Math.round(pnl)).toLocaleString('en-IN')}
            </p>
            {ar.pnl_pct != null && (
              <p style={{ fontSize: 11, fontWeight: 700, color: `${accentColor}cc`, marginTop: 2 }}>
                {parseFloat(ar.pnl_pct) >= 0 ? '+' : ''}{parseFloat(ar.pnl_pct).toFixed(2)}%
              </p>
            )}
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: `${out.color}18`, color: out.color, border: `1px solid ${out.color}30`, display: 'inline-block', marginTop: 4 }}>{out.label}</span>
          </div>
        </div>
      </div>

      {/* ── 1. Global Cues ── */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <SectionHeader Icon={Globe} num="1" title="Global Cues" color="#8b5cf6" />
        <div style={{ padding: '10px 14px 12px' }}>
          {mm && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, background: `${mm.color}12`, border: `1px solid ${mm.color}25`, marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: mm.color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: mm.color }}>{mm.label}</span>
              {mm.summary && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>{mm.summary}</span>}
            </div>
          )}
          {mc.global_indices?.length > 0 && (() => {
            const getSession = (name = '') => {
              const n = name.toLowerCase();
              if (/dow|djia|s&p|sp500|nasdaq|nyse/.test(n)) return 'US';
              if (/ftse|dax|cac|stoxx|euro/.test(n)) return 'EU';
              if (/nikkei|hang seng|hsi|kospi|sgx|asx/.test(n)) return 'Asia';
              return 'Global';
            };
            const SESSION_LABEL = { US: 'Prev Day', EU: 'Prev Day', Asia: 'Same Day', Global: '' };
            const SESSION_COLOR = { US: '#6366f1', EU: '#8b5cf6', Asia: '#06b6d4', Global: '#6b7280' };

            // Group by session for header rows
            const sessions = ['US', 'EU', 'Asia', 'Global'];
            const grouped = {};
            mc.global_indices.slice(0, 10).forEach(idx => {
              const s = getSession(idx.name || idx.symbol);
              if (!grouped[s]) grouped[s] = [];
              grouped[s].push(idx);
            });

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sessions.filter(s => grouped[s]?.length).map(session => (
                  <div key={session}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                      <span style={{ fontSize: 9, fontWeight: 800, color: SESSION_COLOR[session], textTransform: 'uppercase', letterSpacing: '0.07em' }}>{session}</span>
                      {SESSION_LABEL[session] && (
                        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', fontWeight: 600 }}>· {SESSION_LABEL[session]}</span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))', gap: 5 }}>
                      {grouped[session].map((idx, i) => {
                        const chg = parseFloat(idx.change_pct || 0);
                        const col = chg > 0 ? '#16c784' : chg < 0 ? '#ef4444' : '#6b7280';
                        return (
                          <div key={i} style={{ textAlign: 'center', padding: '5px 4px', borderRadius: 7, background: `${col}0e`, border: `1px solid ${col}20` }}>
                            <p style={{ fontSize: 12, fontWeight: 800, color: col }}>{chg >= 0 ? '+' : ''}{chg.toFixed(2)}%</p>
                            <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', marginTop: 2, lineHeight: 1.2 }}>{idx.name || idx.symbol}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          {!mm && !mc.global_indices?.length && (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>Market context not captured</p>
          )}
        </div>
      </div>

      {/* ── 2. App Playbook ── */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <button
          onClick={() => setPlaybookOpen(o => !o)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px 7px', background: '#06b6d40e', borderBottom: playbookOpen ? '1px solid #06b6d418' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        >
          <div style={{ width: 20, height: 20, borderRadius: 6, background: '#06b6d420', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Newspaper size={10} color="#06b6d4" />
          </div>
          <p style={{ fontSize: 10, fontWeight: 800, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1 }}>App Playbook Recommendation</p>
          <span style={{ fontSize: 9, fontWeight: 700, color: '#06b6d480', background: '#06b6d415', borderRadius: 4, padding: '1px 5px', marginRight: 6 }}>2/5</span>
          <span style={{ fontSize: 12, color: '#06b6d4', transition: 'transform 0.2s', display: 'inline-block', transform: playbookOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
        </button>
        {playbookOpen && (
          <div style={{ padding: '10px 14px 12px' }}>
            {regimeLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(6,182,212,0.2)', borderTopColor: '#06b6d4', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Loading regime data…</p>
              </div>
            ) : regime ? (
              <PlaybookResult pb={resolvePlaybookFromRegime(regime, ts)} lite={false} navigate={navigate} />
            ) : (() => {
              const liteRg = deriveLiteRegimeFromCues(mc);
              if (!liteRg) return (
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>No market context captured.</p>
              );
              return <PlaybookResult pb={resolvePlaybookFromRegime(liteRg, ts)} lite={true} navigate={navigate} />;
            })()}
          </div>
        )}
      </div>

      {/* ── 3. User Decision ── */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <SectionHeader Icon={BarChart2} num="3" title="User Decision" color="#f59e0b" />
        <div style={{ padding: '10px 14px 12px' }}>
          {(ts.strategy_tags || ud.tags || []).length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {(ts.strategy_tags || ud.tags || []).map((tag, i) => (
                <span key={i} style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)' }}>{tag}</span>
              ))}
            </div>
          ) : (ud.trade_reason || ts.trade_reason) ? (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{ud.trade_reason || ts.trade_reason}</p>
          ) : (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>No decision tags recorded — add tags to your trades for richer analysis.</p>
          )}
        </div>
      </div>

      {/* ── 4. Trade Setup ── */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <SectionHeader Icon={BarChart2} num="4" title="Trade Setup" color="#6366f1" />
        <div style={{ padding: '10px 14px 12px' }}>
          {/* Entry / SL / Target grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7, marginBottom: 9 }}>
            {[
              ['Entry',     ts.entry_price,  '#eceef5',  'rgba(255,255,255,0.07)',  'rgba(255,255,255,0.15)'],
              ['Stop Loss', ts.stop_loss,    '#ef4444',  'rgba(239,68,68,0.08)',    'rgba(239,68,68,0.2)'],
              ['Target',    ts.target_price, '#16c784',  'rgba(22,199,132,0.08)',   'rgba(22,199,132,0.2)'],
            ].map(([label, val, col, bg, border]) => (
              <div key={label} style={{ padding: '8px 8px 7px', borderRadius: 9, background: bg, border: `1px solid ${border}`, textAlign: 'center' }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: col, lineHeight: 1 }}>
                  {val ? `₹${parseFloat(val).toFixed(2)}` : '—'}
                </p>
                <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.3)', marginTop: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Qty + R:R row */}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center', marginBottom: (ts.strategy_tags || []).length > 0 ? 8 : 0 }}>
            {ts.quantity && (
              <div style={{ padding: '4px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#eceef5' }}>Qty: {ts.quantity}</span>
              </div>
            )}
            {ts.entry_price && ts.stop_loss && ts.target_price && (() => {
              const risk   = Math.abs(parseFloat(ts.entry_price) - parseFloat(ts.stop_loss));
              const reward = Math.abs(parseFloat(ts.target_price) - parseFloat(ts.entry_price));
              const rr     = risk > 0 ? (reward / risk).toFixed(2) : null;
              return rr ? (
                <div style={{ padding: '4px 10px', borderRadius: 7, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#818cf8' }}>R:R = 1:{rr}</span>
                </div>
              ) : null;
            })()}
          </div>

          {/* Strategy tags */}
          {(ts.strategy_tags || []).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {ts.strategy_tags.map((tag, i) => (
                <span key={i} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}>{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 5. Actual Results ── */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <SectionHeader Icon={TrendingUp} num="5" title="Actual Results" color={accentColor} />
        <div style={{ padding: '10px 14px 12px' }}>
          {/* Result grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7, marginBottom: 9 }}>
            {[
              ['Exit',  ar.exit_price ? `₹${parseFloat(ar.exit_price).toFixed(2)}` : '—', '#eceef5'],
              ['P&L',   `${pnlPos ? '+' : ''}₹${Math.abs(Math.round(pnl)).toLocaleString('en-IN')}`, accentColor],
              ['P&L %', ar.pnl_pct != null ? `${parseFloat(ar.pnl_pct) >= 0 ? '+' : ''}${parseFloat(ar.pnl_pct).toFixed(2)}%` : '—', accentColor],
              ['Held',  ar.holding_days ? `${ar.holding_days}d` : '—', '#eceef5'],
            ].map(([label, val, col]) => (
              <div key={label} style={{ padding: '8px 6px 7px', borderRadius: 9, background: label === 'P&L' || label === 'P&L %' ? `${accentColor}0d` : 'rgba(255,255,255,0.04)', border: `1px solid ${label === 'P&L' || label === 'P&L %' ? accentColor + '25' : 'rgba(255,255,255,0.08)'}`, textAlign: 'center' }}>
                <p style={{ fontSize: 12, fontWeight: 800, color: col, lineHeight: 1 }}>{val}</p>
                <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.3)', marginTop: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Outcome + Actual R badges */}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: `${out.color}18`, color: out.color, border: `1px solid ${out.color}35` }}>{out.label}</span>
            {ts.entry_price && ts.stop_loss && ar.exit_price && (() => {
              const riskTaken  = Math.abs(parseFloat(ts.entry_price) - parseFloat(ts.stop_loss));
              const actualMove = parseFloat(ar.exit_price) - parseFloat(ts.entry_price);
              const r = riskTaken > 0 ? (actualMove / riskTaken).toFixed(2) : null;
              const rNum = parseFloat(r);
              const rCol = rNum >= 1 ? '#16c784' : rNum >= 0 ? '#f59e0b' : '#ef4444';
              return r != null ? (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: `${rCol}12`, color: rCol, border: `1px solid ${rCol}30` }}>Actual R = {r}R</span>
              ) : null;
            })()}
          </div>
        </div>
      </div>

      {/* ── 6. AI Analysis ── */}
      <div>
        <SectionHeader Icon={Sparkles} num="6" title="AI Analysis" color="#f59e0b" />
        <div style={{ padding: '10px 14px 12px' }}>
          {s.ai_review ? (
            <div style={{ padding: '9px 11px', borderRadius: 8, background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)', borderLeft: '3px solid #f59e0b' }}>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>{s.ai_review}</p>
            </div>
          ) : (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>AI review not available for this trade.</p>
          )}
        </div>
      </div>

    </div>
  );
}

function AttachmentCard({ type, snapshot }) {
  if (type === 'backtest') return <BacktestCard snapshot={snapshot} />;
  if (type === 'pnl_summary') return <PnLSummaryCard snapshot={snapshot} />;
  if (type === 'discipline') return <DisciplineCard snapshot={snapshot} />;
  if (type === 'trade_history') return <TradeHistoryCard snapshot={snapshot} />;
  if (type === 'trade_analysis') return <TradeAnalysisCard snapshot={snapshot} />;
  if (type === 'capital_tier') return <CapitalTierCard snapshot={snapshot} />;
  if (type === 'decision_engine') return <DecisionEngineCard snapshot={snapshot} />;
  if (type === 'trade_story') return <TradeStoryCard snapshot={snapshot} />;
  return null;
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ForumPostPage() {
  const { colors } = useTheme();
  const { user } = useAuth();
  const { postId } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const fetchPost = async () => {
    try {
      const res = await axios.get(`${API_URL}/forum/posts/${postId}`);
      setPost(res.data);
    } catch { navigate('/forum'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPost(); }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReact = async () => {
    const res = await axios.post(`${API_URL}/forum/posts/${postId}/react`);
    setPost(prev => ({
      ...prev,
      my_reaction: res.data.reacted,
      reaction_count: prev.reaction_count + (res.data.reacted ? 1 : -1),
    }));
  };

  const handleComment = async () => {
    if (!comment.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/forum/posts/${postId}/comments`, { content: comment.trim() });
      const newComment = {
        id: res.data.id,
        user_id: user.user_id,
        author_name: user.full_name,
        content: comment.trim(),
        created_at: res.data.created_at,
      };
      setPost(prev => ({ ...prev, comments: [...(prev.comments || []), newComment], comment_count: prev.comment_count + 1 }));
      setComment('');
    } catch {}
    finally { setSubmitting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    await axios.delete(`${API_URL}/forum/comments/${commentId}`);
    setPost(prev => ({ ...prev, comments: prev.comments.filter(c => c.id !== commentId), comment_count: prev.comment_count - 1 }));
  };

  const handleAdminDeletePost = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    await axios.delete(`${API_URL}/forum/admin/posts/${postId}`);
    navigate('/forum');
  };

  const handleBanUser = async (targetUserId) => {
    const reason = window.prompt('Reason for ban (leave blank for no reason):');
    if (reason === null) return;
    await axios.post(`${API_URL}/forum/admin/bans/${targetUserId}`, { reason: reason || null });
    alert('User banned from the forum.');
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div className="animate-spin" style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid rgba(245,158,11,0.2)', borderTopColor: '#f59e0b' }} />
    </div>
  );

  if (!post) return null;

  const lm = LIFECYCLE_META[post.lifecycle] || LIFECYCLE_META.trade_idea;
  const isAdmin = user?.plan === 'admin';

  return (
    <div style={{ backgroundColor: colors.background, minHeight: '100vh', paddingBottom: 80 }}>
      {/* Nav */}
      <div style={{ padding: '16px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/forum')} style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArrowLeft size={15} color="#eceef5" />
        </button>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: lm.color }}>Strategy Feed</span>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Lifecycle badge + title */}
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: lm.color + '22', color: lm.color, border: `1px solid ${lm.color}40` }}>
            {lm.label}
          </span>
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#eceef5', lineHeight: 1.3, marginBottom: 6 }}>{post.title}</h1>

        {/* Author + time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{ width: 24, height: 24, borderRadius: 7, background: lm.color + '30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: lm.color }}>
            {(post.author_name || 'U')[0].toUpperCase()}
          </div>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{post.author_name}</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>·</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{timeAgo(post.created_at)}</span>
          {isAdmin && (
            <button onClick={() => handleBanUser(post.user_id)} style={{ marginLeft: 'auto', fontSize: 10, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              Ban Author
            </button>
          )}
        </div>

        {/* Description */}
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.65, marginBottom: 14, whiteSpace: 'pre-wrap' }}>
          {post.description}
        </p>

        {/* Instruments + tags */}
        {(post.instruments?.length > 0 || post.tags?.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
            {(post.instruments || []).map((ins, i) => (
              <span key={i} style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>{ins}</span>
            ))}
            {(post.tags || []).map((tag, i) => (
              <span key={i} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>#{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Attachments */}
      {post.attachments?.length > 0 && (
        <div style={{ padding: '14px 16px 0' }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Shared Data</p>
          {post.attachments.map((att, i) => (
            <AttachmentCard key={i} type={att.attach_type} snapshot={att.snapshot} />
          ))}
        </div>
      )}

      {/* Reactions */}
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', margin: '8px 0' }}>
        <button onClick={handleReact} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
          <Heart size={16} fill={post.my_reaction ? '#ef4444' : 'none'} color={post.my_reaction ? '#ef4444' : 'rgba(255,255,255,0.4)'} />
          <span style={{ fontSize: 13, fontWeight: 600, color: post.my_reaction ? '#ef4444' : 'rgba(255,255,255,0.5)' }}>{post.reaction_count}</span>
        </button>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>
          <MessageCircle size={15} />{post.comment_count}
        </span>
        {isAdmin && (
          <button onClick={handleAdminDeletePost} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#ef4444', background: 'none', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 }}>
            <Trash2 size={11} /> Delete Post
          </button>
        )}
      </div>

      {/* Comments */}
      <div style={{ padding: '0 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, marginTop: 8 }}>
          Comments ({post.comment_count})
        </p>
        {(post.comments || []).map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
              {(c.author_name || 'U')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>{c.author_name}</span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>{timeAgo(c.created_at)}</span>
                {(user?.user_id === c.user_id || isAdmin) && (
                  <button onClick={() => handleDeleteComment(c.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    <Trash2 size={10} color="rgba(255,255,255,0.2)" />
                  </button>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{c.content}</p>
            </div>
          </div>
        ))}

        {/* Add comment */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <input
            ref={inputRef}
            value={comment}
            onChange={e => setComment(e.target.value)}
            onKeyPress={e => { if (e.key === 'Enter') handleComment(); }}
            placeholder="Add a comment…"
            style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '9px 12px', fontSize: 13, color: '#eceef5', outline: 'none' }}
          />
          <button
            onClick={handleComment}
            disabled={!comment.trim() || submitting}
            style={{ width: 36, height: 36, borderRadius: 10, background: '#f59e0b', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: !comment.trim() || submitting ? 0.4 : 1 }}>
            <Send size={14} color="#fff" />
          </button>
        </div>
      </div>
    </div>
  );
}
